import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import brStyles from './GiftingCategoryMenuBrandRefresh.module.css';
import { GIFTING_CATEGORY_MENU_REGION } from './giftingCategoryMenuBrandRefreshClasses';
import GiftingCategoryProductRowBrandRefresh from './GiftingCategoryProductRowBrandRefresh';
import GiftingCategoryShippingBannerBrandRefresh from './GiftingCategoryShippingBannerBrandRefresh';
import { handleGiftingCategoryCarouselWheel } from './giftingCategoryProductRowDragScroll';

// Category tab strip only (product row sets its own overscroll/touch-action in CSS)
const scrollContainerStyle = {
  WebkitOverflowScrolling: 'touch',
  overscrollBehaviorX: 'contain',
  overscrollBehaviorY: 'auto',
  touchAction: 'manipulation',
};

function GiftingCategoryMenuBrandRefresh({
  items,
  activeId,
  onItemClick,
  sectionTitle,
  products = [],
  loading = false,
  menuError = false,
  className = '',
  ariaLabel = 'Gifting categories',
}) {
  const listRef = useRef(null);

  // Same wheel helper as product row: vertical scroll passes through; edge horizontal gestures contained
  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;

    const hasHorizontalRoom = () => el.scrollWidth > el.clientWidth + 1;

    const onWheel = (e) => {
      handleGiftingCategoryCarouselWheel(e, el);
    };

    if (hasHorizontalRoom()) {
      el.addEventListener('wheel', onWheel, { passive: false });
    }

    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, [items.length]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section
      id={GIFTING_CATEGORY_MENU_REGION}
      className={`${brStyles.surface} ${className}`.trim()}
      data-region={GIFTING_CATEGORY_MENU_REGION}
    >
      <div className={brStyles.inner}>
        <div className={brStyles.header} data-part="category-header">
          {sectionTitle ? (
            <h2 className={brStyles.title} data-part="category-section-title">
              {sectionTitle}
            </h2>
          ) : null}
          <nav className={brStyles.root} aria-label={ariaLabel}>
            <ul
              ref={listRef}
              className={`no-scrollbar ${brStyles.list}`}
              style={scrollContainerStyle}
              data-part="category-list"
            >
              {items.map((item) => {
          const isActive = activeId === item.id;
          return (
            <li key={item.id} className={brStyles.listItem} data-part="category-item">
              <button
                type="button"
                className={brStyles.item}
                aria-label={item.label}
                aria-pressed={isActive}
                onClick={() => onItemClick(item)}
                data-part={
                  isActive ? 'category-item-active' : 'category-item-inactive'
                }
              >
                <span className={brStyles.ringSlot}>
                  <span
                    className={`${brStyles.ring} ${
                      isActive ? brStyles.ringActive : ''
                    }`.trim()}
                    data-part="category-thumb-ring"
                  >
                    <span
                      className={`${brStyles.thumb} ${
                        isActive ? brStyles.thumbActive : ''
                      }`.trim()}
                      data-part="category-thumb"
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.imageAlt || ''}
                          className={brStyles.thumbImage}
                        />
                      ) : null}
                    </span>
                  </span>
                </span>
                <span
                  className={`${brStyles.label} ${
                    isActive ? brStyles.labelActive : ''
                  }`.trim()}
                  data-part="category-label"
                >
                  {item.label}
                </span>
              </button>
            </li>
          );
        })}
            </ul>
          </nav>
        </div>
        <GiftingCategoryProductRowBrandRefresh
          products={products}
          loading={loading}
          menuError={menuError}
          activeCategoryId={activeId}
        />
      </div>
      <div className={brStyles.promoFooter} data-part="shipping-banner-footer">
        <GiftingCategoryShippingBannerBrandRefresh />
      </div>
    </section>
  );
}

GiftingCategoryMenuBrandRefresh.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      imageAlt: PropTypes.string,
    })
  ).isRequired,
  activeId: PropTypes.string,
  onItemClick: PropTypes.func,
  sectionTitle: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  menuError: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

GiftingCategoryMenuBrandRefresh.defaultProps = {
  activeId: undefined,
  onItemClick: () => {},
  sectionTitle: undefined,
  products: [],
  loading: false,
  menuError: false,
  className: '',
  ariaLabel: 'Gifting categories',
};

export default GiftingCategoryMenuBrandRefresh;
